import axios, { AxiosResponse } from "axios";
import { ArrayFromEnvelope, configService, ObjectFromEnvelope } from "./config.service";
import { Employee, IEmployeePost, IEmployeePut } from "../models/employees";
import { ContactInformation } from "../models/contact_information";
import { convertDateOnlyToUtc } from "../utils/dateUtils";
import { User } from "@/modules/security/models/user-model";
import store from "@/store";
class EmployeesService {
  async fetchEmployees(): Promise<Employee[]> {
    return new Promise((resolve, reject) => {
      axios
        .get<Employee[]>(configService.personaleOrAdminUrl("employees.json"), {
          params: {
            institution_id: configService.institutionId()
          }
        })
        .then((response: AxiosResponse) => {
          resolve(Employee.fromJSONArray(ArrayFromEnvelope<Employee>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }
  async createContactDetails(contactInformation: ContactInformation): Promise<ContactInformation> {
    const url = this.getCreateContactInformationUrl(contactInformation);
    return new Promise((resolve, reject) => {
      axios
        .post<ContactInformation>(url, contactInformation)
        .then((response: AxiosResponse) => {
          resolve(
            ContactInformation.fromJSON(ObjectFromEnvelope<ContactInformation>(response.data))
          );
        })
        .catch((error) => reject(error));
    });
  }

  async updateContactDetails(contactInformation: ContactInformation): Promise<ContactInformation> {
    const url = this.getUpdateContactInformationUrl(contactInformation);
    return new Promise((resolve, reject) => {
      axios
        .put<ContactInformation>(url, {
          contact_information: contactInformation.convertToPutObject()
        })
        .then((response: AxiosResponse) => {
          resolve(
            ContactInformation.fromJSON(ObjectFromEnvelope<ContactInformation>(response.data))
          );
        })
        .catch((error) => reject(error));
    });
  }
  private getUpdateContactInformationUrl(contactInformation: ContactInformation) {
    const user: User = store.getters["security/getUser"]();
    let url = "";
    if (user.isEmployee()) {
      url = configService.APIInstitutionUrl(
        "personale/contact_informations/" +
          contactInformation.id +
          ".json?owner_id=" +
          contactInformation.owner_id
      );
    } else {
      // TODO - Manager skal laves om til at kalde på samme som mebarbejder
      url = configService.APIInstitutionUrl(
        "employees/" +
          contactInformation.owner_id +
          "/contact_informations/" +
          contactInformation.id +
          ".json"
      );
    }
    return url;
  }

  private getCreateContactInformationUrl(contactInformation: ContactInformation) {
    const user: User = store.getters["security/getUser"]();
    let url = "";
    if (user.isEmployee()) {
      url = configService.APIInstitutionUrl(
        "personale/contact_informations.json?owner_id=" + contactInformation.owner_id
      );
    } else {
      // TODO - Manager skal laves om til at kalde på samme som mebarbejder
      url = configService.APIInstitutionUrl(
        "employees/" + contactInformation.owner_id + "/contact_informations.json"
      );
    }
    return url;
  }

  async fetchContactDetails(employeeId: number): Promise<ContactInformation[]> {
    const user: User = store.getters["security/getUser"]();
    let url = "";
    if (user.isEmployee()) {
      url = configService.APIInstitutionUrl(
        "personale/contact_informations.json?owner_id=" + employeeId
      );
    } else {
      // TODO - Manager skal laves om til at kalde på samme som mebarbejder
      url = configService.APIInstitutionUrl(
        "employees/" + employeeId + "/contact_informations.json"
      );
    }
    return new Promise((resolve, reject) => {
      axios
        .get<Employee[]>(url)
        .then((response: AxiosResponse) => {
          resolve(
            ContactInformation.fromJSONArray(ArrayFromEnvelope<ContactInformation>(response.data))
          );
        })
        .catch((error) => reject(error));
    });
  }
  async getPersonByCrn(crn: string): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .post<any>(
          configService.APIInstitutionUrl(
            "employees/get_person_details.json?institution_id=" + configService.institutionId()
          ),
          {
            crn
          }
        )
        .then((response: AxiosResponse) => {
          resolve(ObjectFromEnvelope<any>(response.data));
        })
        .catch((error) => reject(error));
    });
  }
  async createEmployee(employee: IEmployeePost): Promise<Employee> {
    return new Promise((resolve, reject) => {
      axios
        .post<IEmployeePost>(configService.APIInstitutionUrl("employees.json"), employee)
        .then((response: AxiosResponse) => {
          resolve(Employee.fromJSON(ObjectFromEnvelope<Employee>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }
  async updateEmployee(employee: IEmployeePut): Promise<Employee> {
    return new Promise((resolve, reject) => {
      axios
        .put<IEmployeePut>(
          configService.APIInstitutionUrl("employees/" + employee.id + ".json"),
          employee
        )
        .then((response: AxiosResponse) => {
          resolve(Employee.fromJSON(ObjectFromEnvelope<Employee>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }
  async updateEmployeeContactPerson(employee: Employee): Promise<Employee> {
    return new Promise((resolve, reject) => {
      axios
        .post<Employee>(
          configService.APIInstitutionUrl("personale/update_contact_person_details.json"),
          {
            id: employee.id,
            contact_person_details: employee.contact_person_details
          }
        )
        .then((response: AxiosResponse) => {
          resolve(Employee.fromJSON(ObjectFromEnvelope<Employee>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }
  async deleteEmployee(employeeId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .delete<Employee>(
          configService.APIInstitutionUrl("employees/" + employeeId + ".json" + "?cascading=true")
        )
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  }

  async generatePrint(
    employees: Employee[],
    reportTemplateId: number,
    date: Date | undefined,
    weeks: string | undefined,
    months: string | undefined
  ) {
    let url =
      "employees/generate_report.pdf?employees=" +
      employees.map((e) => e.id).toString() +
      "&report_template_id=" +
      reportTemplateId +
      "&institution_id=" +
      configService.institutionId();
    if (date) {
      url += "&date=" + convertDateOnlyToUtc(date);
    } else if (weeks) {
      url += "&weeks=" + weeks;
    } else if (months) {
      url += "&months=" + months;
    }
    return new Promise((resolve, reject) => {
      axios
        .get(configService.APIInstitutionUrl(url))
        .then((response: AxiosResponse) => {
          //Create a Blob from the PDF Stream
          const file = new Blob([response.data as any], {
            type: "application/pdf"
          });
          //Build a URL from the file
          const fileURL = URL.createObjectURL(file);
          resolve(fileURL);
        })
        .catch((error) => reject(error));
    });
  }
}

export const employeeService: EmployeesService = new EmployeesService();
